import React, { useEffect, useState } from "react";
import api, { apiErrorText } from "../lib/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Trash2, Link2 } from "lucide-react";
import { fmtUSD, fmtDate } from "../lib/format";
import { toast } from "sonner";

const PROCESSORS = new Set(["link", "xxpay", "btcpay", "gearspay"]);
const fmtUsdt = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

const KIND_TONE = {
  deposit: "bg-emerald-50 border-emerald-200 text-emerald-700",
  withdrawal: "bg-rose-50 border-rose-200 text-rose-700",
  balance_update: "bg-sky-50 border-sky-200 text-sky-700",
};
const KIND_LABEL = {
  deposit: "Add cash",
  withdrawal: "Withdrawal",
  balance_update: "Balance update",
};

export default function WalletHistorySheet({ wallet, onClose, onMutate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");

  const showScopeFilter = wallet?.type === "chime";

  const load = async () => {
    if (!wallet) return;
    setLoading(true);
    try {
      const params = {};
      if (kindFilter !== "all") params.kind = kindFilter;
      if (scopeFilter !== "all") params.scope = scopeFilter;
      const r = await api.get(`/wallets/${wallet.id}/history`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (wallet) load();
    else setData(null);
    // eslint-disable-next-line
  }, [wallet, kindFilter, scopeFilter]);

  const removeTxn = async (t) => {
    if (!window.confirm("Delete this entry? Balances will be restored.")) return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Entry deleted");
      load();
      onMutate?.();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const subtitle = (() => {
    if (!wallet) return "";
    if (wallet.type === "chime" && data?.linked_cashapps?.length) {
      return `Across this Chime and ${data.linked_cashapps.length} linked CashApp${
        data.linked_cashapps.length > 1 ? "s" : ""
      }.`;
    }
    return "All entries for this wallet.";
  })();

  return (
    <Sheet open={!!wallet} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] p-0 flex flex-col"
        data-testid="wallet-history-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b hairline">
          <SheetTitle className="font-display">{wallet?.name}</SheetTitle>
          <SheetDescription>{subtitle}</SheetDescription>
        </SheetHeader>

        {/* Totals */}
	        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-5 gap-3 border-b hairline">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Current balance
            </div>
            <div className="font-mono text-[18px] font-medium mt-0.5">
              {fmtUSD(wallet?.balance || 0)}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Add cash (sum)
            </div>
            <div className="font-mono text-[18px] font-medium mt-0.5 text-emerald-700">
              {fmtUSD(data?.totals?.deposit_usd || 0)}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
              Withdrawals
            </div>
            <div className="font-mono text-[18px] font-medium mt-0.5 text-rose-700">
              {fmtUSD(data?.totals?.withdrawal_usd || 0)}
            </div>
          </div>
	          <div>
	            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
	              Last balance update
	            </div>
            <div className="font-mono text-[12.5px] font-medium mt-1">
              {data?.last_balance_update ? (
                <>
                  {fmtUSD(data.last_balance_update.new_balance)}{" "}
                  <span className="text-zinc-400">·</span>{" "}
                  <span className="text-zinc-500 text-[11px]">
                    {fmtDate(data.last_balance_update.created_at)}
                  </span>
                </>
              ) : (
                <span className="text-zinc-400">—</span>
              )}
	            </div>
	          </div>
	          <div>
	            <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
	              Last updated
	            </div>
	            <div className="font-mono text-[12.5px] font-medium mt-1">
	              {wallet?.updated_at ? (
	                <span
	                  className={
	                    Date.now() - new Date(wallet.updated_at).getTime() >
	                    7 * 24 * 60 * 60 * 1000
	                      ? "text-amber-700"
	                      : ""
	                  }
	                >
	                  {fmtDate(wallet.updated_at)}
	                </span>
	              ) : (
	                <span className="text-zinc-400">—</span>
	              )}
	            </div>
	          </div>
	        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b hairline flex flex-wrap items-center gap-2">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="h-8 w-[160px]" data-testid="history-kind-filter">
              <SelectValue placeholder="Kind" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="deposit">Add cash</SelectItem>
              <SelectItem value="withdrawal">Withdrawal</SelectItem>
              <SelectItem value="balance_update">Balance update</SelectItem>
            </SelectContent>
          </Select>
          {showScopeFilter && (
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="h-8 w-[180px]" data-testid="history-scope-filter">
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Chime + linked</SelectItem>
                <SelectItem value="self">This Chime only</SelectItem>
                <SelectItem value="linked">Linked CashApps only</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto thin-scroll">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Loading…</div>
          ) : !data?.transactions?.length ? (
            <div className="p-6 text-sm text-zinc-500">No entries yet.</div>
          ) : (
            <ul className="divide-y hairline">
              {data.transactions.map((t) => (
                <li
                  key={t.id}
                  className="px-6 py-3 flex items-start gap-3"
                  data-testid={`history-item-${t.id}`}
                >
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${
                      KIND_TONE[t.kind] || "bg-zinc-100 border-zinc-200 text-zinc-600"
                    }`}
                  >
                    {KIND_LABEL[t.kind] || t.kind}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[13px] font-medium truncate">
                        {(() => {
                          const isProc = PROCESSORS.has(t.wallet_type);
                          if (t.kind === "balance_update") {
                            return isProc ? (
                              <>
                                <span className="font-mono">{fmtUsdt(t.old_balance)}</span>
                                <span className="mx-1 text-zinc-400">→</span>
                                <span className="font-mono">
                                  {fmtUsdt(t.new_balance)} USDT
                                </span>
                                {t.amount_usd != null && (
                                  <span className="ml-2 text-[11.5px] text-emerald-700 font-mono">
                                    ({t.amount_usd >= 0 ? "+" : ""}
                                    {fmtUSD(t.amount_usd)})
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="font-mono">{fmtUSD(t.old_balance)}</span>
                                <span className="mx-1 text-zinc-400">→</span>
                                <span className="font-mono">{fmtUSD(t.new_balance)}</span>
                              </>
                            );
                          }
                          if (isProc) {
                            return (
                              <>
                                <span className="font-mono">
                                  {fmtUsdt(t.amount_usdt)} USDT
                                </span>
                                <span className="ml-2 text-[11.5px] text-emerald-700 font-mono">
                                  ({fmtUSD(t.amount_usd)})
                                </span>
                              </>
                            );
                          }
                          return <span className="font-mono">{fmtUSD(t.amount_usd)}</span>;
                        })()}
                        {t.kind === "withdrawal" &&
                          !PROCESSORS.has(t.wallet_type) &&
                          t.amount_usdt != null && (
                            <span className="ml-2 text-[11.5px] text-zinc-500 font-mono">
                              (
                              {t.amount_usdt.toLocaleString("en-US", {
                                maximumFractionDigits: 4,
                              })}{" "}
                              USDT @ {t.rate?.toFixed(4)})
                            </span>
                          )}
                      </div>
                      <button
                        onClick={() => removeTxn(t)}
                        className="p-1 rounded hover:bg-rose-50 text-rose-600 shrink-0"
                        title="Delete"
                        data-testid={`history-delete-${t.id}`}
                      >
                        <Trash2 size={12} strokeWidth={1.7} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11.5px] text-zinc-500">
                      <span className="font-mono">{fmtDate(t.created_at)}</span>
                      {t.scope === "linked" && (
                        <span className="inline-flex items-center gap-0.5">
                          <Link2 size={10} strokeWidth={1.8} /> {t.wallet_name}
                        </span>
                      )}
                      {t.created_by_name && <span>· {t.created_by_name}</span>}
                    </div>
                    {t.notes && (
                      <div className="text-[12px] text-zinc-600 mt-1">{t.notes}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
